<script lang="ts">
  import { onDestroy } from 'svelte'
  import { debounce } from 'es-toolkit'
  import useObjectQueries from '../../../hooks/useObjectQueries'
  import {
    ThreeItemKeys,
    ThreeSpiralModes,
  } from '../../../../../constants/three'
  import i18n from '../../../../../i18n'
  import { isObjectDxf2D } from '../../../../../helpers/scene-json'
  import Select from '../../../../../components/select/Select.svelte'
  import InputLabel from '../InputLabel.svelte'
  import SectionContainer from '../SectionContainer.svelte'

  type Props = { objectId: string }
  const { objectId }: Props = $props()

  let layersGroupAmount = $state<number>()
  let layersAmount = $state<number>()
  let layerHeight = $state<number>()
  let spiralMode = $state<string>()
  let invertPrintDirection = $state<boolean>()
  let avoidInsideTravelling = $state<boolean>()
  let avoidInsideTravellingMarginMm = $state<number>()
  let isSyncingFromGroup = false

  const { updateUserDataMutation, useObject } = useObjectQueries()
  let groupQuery = $derived(useObject(objectId))
  let group = $derived(groupQuery.data)
  let isDxf2D = $derived(group && isObjectDxf2D(group))

  const onStateUpdate = debounce(
    (
      targetObjectId: string,
      props: Record<string, number | string | boolean | undefined>,
    ) => {
      updateUserDataMutation.mutate({
        objectUuid: targetObjectId,
        object: props,
      })
    },
    500,
  )

  onDestroy(() => onStateUpdate.cancel())

  $effect(() => {
    isSyncingFromGroup = true
    layersAmount = group?.userData[ThreeItemKeys.LAYERS_AMOUNT]
    layerHeight = group?.userData[ThreeItemKeys.LAYER_HEIGHT]
    layersGroupAmount = group?.userData[ThreeItemKeys.LAYERS_GROUP_AMOUNT]
    spiralMode = group?.userData[ThreeItemKeys.PRINTING_SPIRAL_MODE]
    invertPrintDirection
      = group?.userData[ThreeItemKeys.INVERT_PRINT_DIRECTION] || false
    avoidInsideTravelling
      = group?.userData[ThreeItemKeys.AVOID_INSIDE_TRAVELLING] || false
    avoidInsideTravellingMarginMm
      = group?.userData[ThreeItemKeys.AVOID_INSIDE_TRAVELLING_MARGIN_MM]
  })

  $effect(() => {
    const props = {
      [ThreeItemKeys.LAYERS_AMOUNT]: layersAmount,
      [ThreeItemKeys.LAYER_HEIGHT]: layerHeight,
      [ThreeItemKeys.LAYERS_GROUP_AMOUNT]: layersGroupAmount,
      [ThreeItemKeys.PRINTING_SPIRAL_MODE]: spiralMode,
      [ThreeItemKeys.INVERT_PRINT_DIRECTION]: invertPrintDirection,
      [ThreeItemKeys.AVOID_INSIDE_TRAVELLING]: avoidInsideTravelling,
      [ThreeItemKeys.AVOID_INSIDE_TRAVELLING_MARGIN_MM]:
        avoidInsideTravellingMarginMm,
    }

    if (isSyncingFromGroup) {
      isSyncingFromGroup = false
      return
    }

    onStateUpdate(objectId, props)
  })
</script>

<SectionContainer title={$i18n.t('project.rightBar.printSettings.title')}>
  <div class="container">
    {#if isDxf2D}
      <div class="row">
        <InputLabel
        >{$i18n.t('project.rightBar.printSettings.layersAmount')}</InputLabel
        >
        <div class="input-container">
          <input min={1} type="number" bind:value={layersAmount} />
        </div>
      </div>
    {/if}
    <div class="row">
      <InputLabel
      >{$i18n.t('project.rightBar.printSettings.layerHeight')}</InputLabel
      >
      <div class="input-container">
        <input type="number" bind:value={layerHeight} />
      </div>
    </div>
    {#if layersAmount && layerHeight && isDxf2D}
      <div class="row">
        <InputLabel
        >{$i18n.t('project.rightBar.printSettings.totalHeight')}</InputLabel
        >
        <div class="input-container">
          <input disabled type="number" value={layersAmount * layerHeight} />
        </div>
      </div>
    {/if}
    <div class="row">
      <InputLabel>
        {$i18n.t('project.rightBar.printSettings.layersGroupAmount')}
      </InputLabel>
      <div class="input-container">
        <input type="number" bind:value={layersGroupAmount} />
      </div>
    </div>
    <div class="row">
      <p>
        {$i18n.t('project.rightBar.printSettings.spiralMode')}
      </p>
      <div class="input-container">
        <Select
          name="spiral-mode"
          options={[
            {
              value: ThreeSpiralModes.OFF,
              label: $i18n.t('project.rightBar.printSettings.spiralModes.off'),
            },
            {
              value: ThreeSpiralModes.SEMI_SPIRAL,
              label: $i18n.t(
                'project.rightBar.printSettings.spiralModes.semiSpiral',
              ),
            },
            {
              value: ThreeSpiralModes.SPIRAL,
              label: $i18n.t(
                'project.rightBar.printSettings.spiralModes.spiral',
              ),
            },
          ]}
          placeholder={$i18n.t(
            'project.rightBar.printSettings.spiralModes.off',
          )}
          bind:value={spiralMode}
        />
      </div>
    </div>

    <div class="row">
      <InputLabel>
        {$i18n.t('project.rightBar.printSettings.invertPrintDirection')}
      </InputLabel>
      <div class="input-container">
        <Select
          name="invert-print-direction"
          options={[
            {
              value: false,
              label: $i18n.t('globals.no'),
            },
            {
              value: true,
              label: $i18n.t('globals.yes'),
            },
          ]}
          bind:value={invertPrintDirection}
        />
      </div>
    </div>

    <div class="row">
      <InputLabel>
        {$i18n.t('project.rightBar.printSettings.avoidInsideTravelling')}
      </InputLabel>
      <div class="input-container">
        <Select
          name="avoid-inside-travelling"
          options={[
            {
              value: false,
              label: $i18n.t('globals.no'),
            },
            {
              value: true,
              label: $i18n.t('globals.yes'),
            },
          ]}
          bind:value={avoidInsideTravelling}
        />
      </div>
    </div>

    {#if avoidInsideTravelling}
      <div class="row">
        <InputLabel>
          {$i18n.t(
            'project.rightBar.printSettings.avoidInsideTravellingMarginMm',
          )}
        </InputLabel>
        <div class="input-container">
          <input type="number" bind:value={avoidInsideTravellingMarginMm} />
        </div>
      </div>
    {/if}
  </div>
</SectionContainer>

<style lang="scss">
  .container {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .row {
    align-items: center;
    display: flex;
    gap: 1rem;
    justify-content: space-between;

    & > p {
      display: flex;
      color: #aaaaaa;
      font-size: 0.75rem;
      font-weight: 500;
    }

    .input-container {
      align-items: stretch;
      display: flex;
      flex-direction: column;
      width: 6rem;

      & > input {
        background-color: var(--translation-input-background);
        border-radius: 0.25rem;
        border: 1px solid var(--translation-input-background);
        caret-color: var(--translation-input-foreground);
        color: var(--translation-input-foreground);
        font-size: 0.75rem;
        margin: 0;
        min-height: 2rem;
        outline: none;
        text-align: center;

        &::-webkit-inner-spin-button,
        &::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
      }
    }
  }
</style>
